import { useState, type ReactNode } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Alert,
  Autocomplete,
  Button,
  FormControlLabel,
  Link,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";
import z from "zod";

import {
  AttachOrganisationsRequestSchema,
  CHOOSABLE_ORGANISATION_TYPES,
  CustomerRoleSchema,
  DIRECTORY_PAGE_LIMIT_DEFAULT,
  GrantMembershipRequestSchema,
  isOrganisationChangeable,
  OrganisationRelationSchema,
  RelateOrganisationRequestSchema,
  RenameOrganisationRequestSchema,
  SetOrganisationTypeRequestSchema,
  SetReadsApprovedAssessmentsThroughMembersRequestSchema,
  type AdministeredOrganisation,
  type ConfigurationScopeId,
  type CustomerRole,
  type Organisation,
  type OrganisationId,
  type OrganisationRelation,
  type OrganisationRelationshipId,
  type OrganisationRelationships,
  type OrganisationUserMemberships,
  type RelateOrganisationRequest,
  type RenameOrganisationRequest,
  type SetOrganisationTypeRequest,
  type SetReadsApprovedAssessmentsThroughMembersRequest,
  type User,
  type UserId,
} from "@thalamos/haven-common";

import api, { apiHooks } from "../../api/index.js";
import { createRoute } from "../../routing/createRoute.js";
import type {
  Loaded,
  OrganisationChangeOutcome,
} from "./adminOrganisationState.js";
import type { AdminOrganisationTab } from "./adminOrganisationTabs.js";
import {
  ADMIN_ORGANISATION_TABS,
  adminOrganisationTabOf,
} from "./adminOrganisationTabs.js";
import type { AccountLoadFailure } from "./AdminUserPage.js";
import { accountLoadFailure } from "./AdminUserPage.js";
import { ConfirmChangesDialog } from "./ConfirmChangesDialog.js";
import {
  OrganisationConfigurationTab,
  type OrganisationConfigurationData,
} from "./OrganisationConfigurationTab.js";
import {
  RelationshipChangeDialog,
  type RelationshipChange,
} from "./RelationshipChangeDialog.js";
import {
  OrganisationStructureTab,
  type LoadedStructure,
} from "./structure/OrganisationStructureTab.js";
import { useDirectoryQuery } from "./useDirectoryQuery.js";
import {
  readScopeSettingsFromApi,
  type ReadScopeSettings,
} from "./useSettingsOfScopes.js";

type OrganisationUserMembership =
  OrganisationUserMemberships["memberships"][number];
type Relationship = OrganisationRelationships["relationships"][number];

/**
 * Whether a relationship was changed, and if it was refused, the server's
 * reason. The reason is shown as it is, because only the server can tell a
 * duplicate or a loop through organisations the page has not read.
 */
export type RelationshipChangeOutcome =
  { outcome: "changed" } | { outcome: "failed"; reason: string | undefined };

/** What the Basic details form saves; `null` for a part that did not change. */
export type OrganisationDetailsChanges = {
  renameRequest: RenameOrganisationRequest | null;
  setTypeRequest: SetOrganisationTypeRequest | null;
  setReadsRequest: SetReadsApprovedAssessmentsThroughMembersRequest | null;
};

/** The kinds a relationship is added as, in the order the picker offers them. */
const RELATIONS = OrganisationRelationSchema.options;

/** Every customer role, admin included, since this tab lists every membership. */
const GRANTABLE_ROLES = CustomerRoleSchema.options;

const tabId = (tab: AdminOrganisationTab) => `admin-organisation-tab-${tab}`;

const RefusalSchema = z.object({ message: z.string() });

/** The server's reason for a refusal, where it gave one. */
const refusalReason = (data: unknown): string | undefined =>
  RefusalSchema.safeParse(data).data?.message;

/**
 * Re-reads after a saved change. A failed re-read shows as that read's own
 * failure, never as a failed change.
 */
const rereadAfterSaving = (reread: () => Promise<unknown>) =>
  reread().catch(() => undefined);

/** One organisation, as Thalamos administers it. */
export const AdminOrganisationPage = () => {
  const { organisationId, tab } = useParams();

  // `useParams` types every segment as optional, but the route cannot match
  // without this one. The fallback is there to satisfy that type rather than to
  // describe a state the page can be in.
  /* v8 ignore next */
  const requestedId = organisationId ?? "";
  const openTab = adminOrganisationTabOf(tab) ?? "details";

  const [{ data, loading, error, response }, refetch] =
    apiHooks.admin.organisation(requestedId);
  const [relationships, refetchRelationships] =
    apiHooks.admin.organisationRelationships(requestedId);
  const [memberships, refetchMemberships] =
    apiHooks.admin.organisationUserMemberships(requestedId);
  const [structure, refetchStructure] = apiHooks.admin.organisationStructure(
    requestedId,
    { manual: openTab !== "structure" },
  );

  // Read only while the Configuration tab is open, the one place they show.
  const configurationOpen = openTab === "configuration";
  const [scopes, refetchScopes] = apiHooks.configuration.scopes({
    manual: !configurationOpen,
  });
  const [organisationSettings, refetchOrganisationSettings] =
    apiHooks.configuration.organisationSettings(requestedId, {
      manual: !configurationOpen,
    });

  // Each picker searches only while a tab showing it is open: the requests are
  // the expensive directory searches, and other tabs have no use for the answer.
  const organisationQuery = useDirectoryQuery();
  const [{ data: organisationOptions }] = apiHooks.admin.organisations(
    { query: organisationQuery.query, limit: DIRECTORY_PAGE_LIMIT_DEFAULT },
    { manual: openTab !== "relationships" && openTab !== "structure" },
  );
  const userQuery = useDirectoryQuery();
  const [{ data: userOptions }] = apiHooks.admin.users(
    { query: userQuery.query, limit: DIRECTORY_PAGE_LIMIT_DEFAULT },
    { manual: openTab !== "users" },
  );

  // A 4xx body resolves onto `data`, so an organisation is only ever taken
  // from a 200.
  const organisation = response?.status === 200 ? data : undefined;

  // Changes go to the organisation the server returned, never to the id in the
  // address.
  const organisationIdToChange = (): OrganisationId => {
    // Unreachable: the change controls render only while `organisation` is
    // defined, and a failed re-read swaps them for the load failure. Kept to
    // fail loudly if not.
    /* v8 ignore if */
    if (organisation === undefined) {
      throw new Error("Expected a change only once the organisation is read");
    }

    return organisation.id;
  };

  /**
   * Saves what changed, one request per field the server keeps apart, and
   * stops at the first refusal. Re-reads either way, so the page shows what
   * the server now holds, including any change made before a refusal.
   */
  const updateDetails = async (
    current: AdministeredOrganisation,
    {
      renameRequest,
      setTypeRequest,
      setReadsRequest,
    }: OrganisationDetailsChanges,
  ): Promise<OrganisationChangeOutcome> => {
    const sends = [
      renameRequest === null
        ? null
        : () => api.admin.renameOrganisation(current.id, renameRequest),
      setTypeRequest === null
        ? null
        : () => api.admin.setOrganisationType(current.id, setTypeRequest),
      setReadsRequest === null
        ? null
        : () =>
            api.admin.setReadsApprovedAssessmentsThroughMembers(
              current.id,
              setReadsRequest,
            ),
    ];

    let outcome: OrganisationChangeOutcome = "changed";

    try {
      for (const change of sends) {
        if (change !== null && outcome === "changed") {
          const changed = await change();
          outcome = changed.status >= 400 ? "failed" : "changed";
        }
      }
    } catch {
      outcome = "failed";
    }

    await rereadAfterSaving(refetch);

    return outcome;
  };

  const changeRelationship = async (
    request: Promise<{ status: number; data: unknown }>,
  ): Promise<RelationshipChangeOutcome> => {
    const changed = await request;

    if (changed.status >= 400) {
      return { outcome: "failed", reason: refusalReason(changed.data) };
    }

    await rereadAfterSaving(refetchRelationships);
    // Off its own tab the structure is never read, and opening the tab reads it.
    if (openTab === "structure") {
      await rereadAfterSaving(refetchStructure);
    }
    return { outcome: "changed" };
  };

  /**
   * Grants and removals go through the account's own endpoints, so this tab
   * and the account's page change one set of memberships.
   */
  const changeMembership = async (
    request: Promise<{ status: number }>,
  ): Promise<OrganisationChangeOutcome> => {
    const changed = await request;

    if (changed.status >= 400) {
      return "failed";
    }

    await rereadAfterSaving(refetchMemberships);
    return "changed";
  };

  const setConfigurationScope = async (
    configurationScopeId: ConfigurationScopeId | null,
  ): Promise<OrganisationChangeOutcome> => {
    try {
      const changed = await api.configuration.attachOrganisations(
        AttachOrganisationsRequestSchema.parse({
          organisations: [
            { organisationId: organisationIdToChange(), configurationScopeId },
          ],
        }),
      );

      if (changed.status >= 400) {
        return "failed";
      }
    } catch {
      return "failed";
    }

    // Every count of who uses a configuration moves with this one.
    await rereadAfterSaving(() =>
      Promise.all([refetch(), refetchScopes(), refetchOrganisationSettings()]),
    );
    return "changed";
  };

  return (
    <AdminOrganisationPageContent
      organisation={organisation}
      loading={loading}
      failure={accountLoadFailure(
        response?.status,
        error !== undefined && error !== null,
      )}
      tab={openTab}
      structure={{
        data: structure.response?.status === 200 ? structure.data : undefined,
        failed:
          accountLoadFailure(
            structure.response?.status,
            structure.error !== undefined && structure.error !== null,
          ) !== undefined,
      }}
      relationships={{
        rows:
          relationships.response?.status === 200
            ? relationships.data?.relationships
            : undefined,
        failed:
          accountLoadFailure(
            relationships.response?.status,
            relationships.error !== undefined && relationships.error !== null,
          ) !== undefined,
      }}
      memberships={{
        rows:
          memberships.response?.status === 200
            ? memberships.data?.memberships
            : undefined,
        failed:
          accountLoadFailure(
            memberships.response?.status,
            memberships.error !== undefined && memberships.error !== null,
          ) !== undefined,
      }}
      configuration={{
        scopes: {
          rows:
            scopes.response?.status === 200 ? scopes.data?.scopes : undefined,
          failed:
            accountLoadFailure(
              scopes.response?.status,
              scopes.error !== undefined && scopes.error !== null,
            ) !== undefined,
        },
        settings: {
          holderConfiguration:
            organisationSettings.response?.status === 200
              ? (organisationSettings.data ?? null)
              : null,
          loading: organisationSettings.loading,
        },
      }}
      onSetConfigurationScope={setConfigurationScope}
      readScopeSettings={readScopeSettingsFromApi}
      onUpdateDetails={updateDetails}
      organisationOptions={organisationOptions?.results ?? []}
      organisationSearch={organisationQuery.input}
      onOrganisationSearch={organisationQuery.setQuery}
      onRelate={(request) =>
        changeRelationship(
          api.admin.relateOrganisation(organisationIdToChange(), request),
        )
      }
      onUnrelate={(relationshipId) =>
        changeRelationship(
          api.admin.unrelateOrganisation(
            organisationIdToChange(),
            relationshipId,
          ),
        )
      }
      userOptions={userOptions?.results ?? []}
      userSearch={userQuery.input}
      onUserSearch={userQuery.setQuery}
      onGrant={(userId, role) =>
        changeMembership(
          api.user.grantMembership(
            userId,
            GrantMembershipRequestSchema.parse({
              organisationId: organisationIdToChange(),
              role,
            }),
          ),
        )
      }
      onRevoke={(userId, role) =>
        changeMembership(
          api.user.revokeMembership(userId, organisationIdToChange(), role),
        )
      }
    />
  );
};

/**
 * The page's rendering, with what it shows and every change injected. Mounting
 * it needs no API client, so a story or a test can show any state it has.
 */
export const AdminOrganisationPageContent = ({
  organisation,
  loading,
  failure,
  tab,
  structure,
  relationships,
  memberships,
  configuration,
  onSetConfigurationScope,
  readScopeSettings,
  onUpdateDetails,
  organisationOptions,
  organisationSearch,
  onOrganisationSearch,
  onRelate,
  onUnrelate,
  userOptions,
  userSearch,
  onUserSearch,
  onGrant,
  onRevoke,
}: {
  organisation: AdministeredOrganisation | undefined;
  loading: boolean;
  failure: AccountLoadFailure | undefined;
  tab: AdminOrganisationTab;
  structure: LoadedStructure;

  relationships: Loaded<Relationship>;
  memberships: Loaded<OrganisationUserMembership>;
  configuration: OrganisationConfigurationData;
  onSetConfigurationScope: (
    configurationScopeId: ConfigurationScopeId | null,
  ) => Promisable<OrganisationChangeOutcome>;
  readScopeSettings: ReadScopeSettings;
  onUpdateDetails: (
    current: AdministeredOrganisation,
    changes: OrganisationDetailsChanges,
  ) => Promisable<OrganisationChangeOutcome>;
  /** The organisations a relationship may name, as the current search returned them. */
  organisationOptions: readonly Organisation[];
  organisationSearch: string;
  onOrganisationSearch: (query: string) => Promisable<void>;
  onRelate: (
    request: RelateOrganisationRequest,
  ) => Promisable<RelationshipChangeOutcome>;
  onUnrelate: (
    relationshipId: OrganisationRelationshipId,
  ) => Promisable<RelationshipChangeOutcome>;
  /** The accounts a membership may be granted to, as the current search returned them. */
  userOptions: readonly User[];
  userSearch: string;
  onUserSearch: (query: string) => Promisable<void>;
  onGrant: (
    userId: UserId,
    role: CustomerRole,
  ) => Promisable<OrganisationChangeOutcome>;
  onRevoke: (
    userId: UserId,
    role: CustomerRole,
  ) => Promisable<OrganisationChangeOutcome>;
}) => {
  const { t } = useTranslation();

  // A count only once the rows are read: "Users (0)" while they are loading
  // would say the organisation has nobody.
  const counted = (label: string, rows: readonly unknown[] | undefined) =>
    rows === undefined
      ? label
      : t("admin.organisation.tabs.counted", { label, count: rows.length });

  const tabLabel = (each: AdminOrganisationTab) =>
    match(each)
      .with("details", () => t("admin.organisation.tabs.details"))
      .with("relationships", () =>
        counted(t("admin.organisation.tabs.relationships"), relationships.rows),
      )
      .with("users", () =>
        counted(t("admin.organisation.tabs.users"), memberships.rows),
      )
      .with("configuration", () => t("admin.organisation.tabs.configuration"))
      .with("structure", () => t("admin.organisation.tabs.structure"))
      .exhaustive();

  return (
    <Stack spacing={3}>
      <Button
        component={RouterLink}
        to={createRoute.adminOrganisations()}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ alignSelf: "flex-start" }}
      >
        {t("admin.organisation.backToList")}
      </Button>

      <Typography variant="h5" component="h1">
        {organisation === undefined
          ? t("admin.organisation.title")
          : organisation.name}
      </Typography>

      {/* Only before the first read: a re-read after a change would otherwise
          unmount the tab, and with it what the change has to say. */}
      {loading && organisation === undefined ? (
        <Typography variant="body2" color="text.secondary">
          {t("common.loading")}
        </Typography>
      ) : organisation === undefined ? (
        <Typography variant="body2" color="error">
          {failure === "not_found"
            ? t("admin.organisation.notFound")
            : t("admin.organisation.loadFailed")}
        </Typography>
      ) : (
        <>
          <Tabs
            value={tab}
            aria-label={t("admin.organisation.tabs.label")}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            {ADMIN_ORGANISATION_TABS.map((each) => (
              <Tab
                key={each}
                value={each}
                id={tabId(each)}
                aria-controls="admin-organisation-tabpanel"
                label={tabLabel(each)}
                component={RouterLink}
                to={createRoute.adminOrganisation(organisation.id, each)}
              />
            ))}
          </Tabs>

          <Stack
            spacing={2}
            role="tabpanel"
            id="admin-organisation-tabpanel"
            aria-labelledby={tabId(tab)}
          >
            {match(tab)
              .with("details", () => (
                <BasicDetails
                  // Keyed on the organisation alone, so the re-read after a
                  // save that failed part-way does not start the form again and
                  // drop the failure it is showing.
                  key={organisation.id}
                  organisation={organisation}
                  onUpdate={(changes) => onUpdateDetails(organisation, changes)}
                />
              ))
              .with("relationships", () => (
                <RelationshipsTab
                  organisation={organisation}
                  relationships={relationships}
                  organisationOptions={organisationOptions}
                  organisationSearch={organisationSearch}
                  onOrganisationSearch={onOrganisationSearch}
                  onRelate={onRelate}
                  onUnrelate={onUnrelate}
                />
              ))
              .with("users", () => (
                <UsersTab
                  organisation={organisation}
                  memberships={memberships}
                  userOptions={userOptions}
                  userSearch={userSearch}
                  onUserSearch={onUserSearch}
                  onGrant={onGrant}
                  onRevoke={onRevoke}
                />
              ))
              .with("configuration", () => (
                <OrganisationConfigurationTab
                  // Keyed on the organisation and what it is set to, so the
                  // picker starts again from the configuration the server now
                  // holds, and never carries a choice to another organisation.
                  key={`${organisation.id}:${organisation.configurationScope?.id ?? "none"}`}
                  organisation={organisation}
                  configuration={configuration}
                  onSetConfigurationScope={onSetConfigurationScope}
                  readScopeSettings={readScopeSettings}
                />
              ))
              .with("structure", () => (
                <OrganisationStructureTab
                  key={organisation.id}
                  organisation={organisation}
                  structure={structure}
                  addRelationship={
                    <AddRelationship
                      organisation={organisation}
                      organisationOptions={organisationOptions}
                      organisationSearch={organisationSearch}
                      onOrganisationSearch={onOrganisationSearch}
                      onRelate={onRelate}
                    />
                  }
                />
              ))
              .exhaustive()}
          </Stack>
        </>
      )}
    </Stack>
  );
};

/** A label above the value it names, read-only. */
const Detail = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Stack spacing={0.5}>
    <Typography variant="subtitle2" component="dt">
      {label}
    </Typography>
    <Typography variant="body1" component="dd" sx={{ m: 0 }}>
      {children}
    </Typography>
  </Stack>
);

/**
 * A change of kind to one somebody has decided on: `unknown` is never saved as
 * a choice.
 */
const DecidedTypeRequestSchema = SetOrganisationTypeRequestSchema.refine(
  (request) => request.type !== "unknown",
);

/**
 * Name, kind and whether it reads through its members, edited together and
 * saved by one Update.
 *
 * The Thalamos organisation's name and kind are shown and not offered: nothing
 * the rules grant may change them. Its setting is a configuration setting,
 * which administration may set everywhere, so it stays offered.
 */
const BasicDetails = ({
  organisation,
  onUpdate,
}: {
  organisation: AdministeredOrganisation;
  onUpdate: (
    changes: OrganisationDetailsChanges,
  ) => Promisable<OrganisationChangeOutcome>;
}) => {
  const { t } = useTranslation();
  const changeable = isOrganisationChangeable(organisation.id);
  const [name, setName] = useState<string>(organisation.name);
  const [type, setType] = useState<string>(
    organisation.type === "unknown" ? "" : organisation.type,
  );
  const [reads, setReads] = useState(
    organisation.readsApprovedAssessmentsThroughMembers,
  );
  const [status, setStatus] = useState<"saving" | "failed">();
  const [confirmingReads, setConfirmingReads] = useState(false);

  const parsedName = RenameOrganisationRequestSchema.safeParse({ name });
  const parsedType = DecidedTypeRequestSchema.safeParse({ type });
  const nameProblem = parsedName.error?.issues[0]?.message;
  const readsChange =
    reads === organisation.readsApprovedAssessmentsThroughMembers
      ? null
      : SetReadsApprovedAssessmentsThroughMembersRequestSchema.parse({
          value: reads,
        });

  const changes: OrganisationDetailsChanges | undefined = !changeable
    ? {
        renameRequest: null,
        setTypeRequest: null,
        setReadsRequest: readsChange,
      }
    : parsedName.success && parsedType.success
      ? {
          renameRequest:
            parsedName.data.name === organisation.name ? null : parsedName.data,
          setTypeRequest:
            parsedType.data.type === organisation.type ? null : parsedType.data,
          setReadsRequest: readsChange,
        }
      : undefined;

  const changed =
    changes !== undefined &&
    (changes.renameRequest !== null ||
      changes.setTypeRequest !== null ||
      changes.setReadsRequest !== null);

  const update = async (): Promise<OrganisationChangeOutcome> => {
    // Unreachable: Update is disabled unless `changed`, which needs `changes`,
    // and the reads confirmation is modal, so the name and type cannot fail
    // meanwhile. Kept so a drift fails politely.
    /* v8 ignore if */
    if (changes === undefined) {
      return "failed";
    }

    setStatus("saving");
    let outcome: OrganisationChangeOutcome;

    try {
      outcome = await onUpdate(changes);
    } catch {
      outcome = "failed";
    }

    if (outcome === "changed" && parsedName.success && parsedType.success) {
      // The re-read does not rebuild the form, so show the values as saved,
      // trimmed as the request sent them.
      setName(parsedName.data.name);
      setType(parsedType.data.type);
    }
    setStatus(outcome === "failed" ? "failed" : undefined);
    return outcome;
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      {changeable ? (
        <Stack spacing={2}>
          <TextField
            required
            label={t("admin.organisation.name")}
            value={name}
            error={nameProblem !== undefined}
            helperText={nameProblem === undefined ? undefined : t(nameProblem)}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          <TextField
            select
            required
            label={t("admin.organisation.type")}
            value={type}
            onChange={(event) => {
              setType(event.target.value);
            }}
          >
            {CHOOSABLE_ORGANISATION_TYPES.map((each) => (
              <MenuItem key={each} value={each}>
                {t(`organisation.type.${each}`)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      ) : null}

      <Stack component="dl" spacing={2} sx={{ m: 0 }}>
        {changeable ? null : (
          <>
            <Detail label={t("admin.organisation.name")}>
              {organisation.name}
            </Detail>
            <Detail label={t("admin.organisation.type")}>
              {t(`organisation.type.${organisation.type}`)}
            </Detail>
          </>
        )}
        <Detail label={t("admin.organisation.configuration.label")}>
          <Stack spacing={0.5} component="span">
            {organisation.configurationScope === null ? (
              <span>{t("admin.organisation.configuration.none")}</span>
            ) : (
              <Link
                component={RouterLink}
                to={createRoute.adminConfigurationScope(
                  organisation.configurationScope.id,
                )}
              >
                {organisation.configurationScope.name}
              </Link>
            )}
            <Link
              component={RouterLink}
              to={createRoute.adminOrganisation(
                organisation.id,
                "configuration",
              )}
              variant="body2"
            >
              {t("admin.organisation.configuration.changeOnTab")}
            </Link>
          </Stack>
        </Detail>
      </Stack>

      <Stack spacing={1}>
        <FormControlLabel
          control={
            <Switch
              checked={reads}
              onChange={(event) => {
                setReads(event.target.checked);
              }}
            />
          }
          label={t("admin.organisation.readsThroughMembers.label")}
        />
        <Typography variant="body2" color="text.secondary">
          {t("admin.organisation.readsThroughMembers.description")}
        </Typography>
      </Stack>

      {status === "failed" && !confirmingReads ? (
        <Alert severity="error">{t("admin.organisation.updateFailed")}</Alert>
      ) : null}

      <Button
        variant="contained"
        disabled={!changed || status === "saving"}
        sx={{ alignSelf: "flex-start" }}
        onClick={() => {
          // Reading assessments through members changes what this
          // organisation's users can read, so it is confirmed first.
          if (reads === organisation.readsApprovedAssessmentsThroughMembers) {
            void update();
          } else {
            setStatus(undefined);
            setConfirmingReads(true);
          }
        }}
      >
        {t("admin.organisation.update")}
      </Button>

      <ConfirmChangesDialog
        open={confirmingReads}
        title={t(
          reads
            ? "admin.organisation.readsThroughMembers.confirmOnTitle"
            : "admin.organisation.readsThroughMembers.confirmOffTitle",
          { name: organisation.name },
        )}
        confirmLabel={t("admin.organisation.readsThroughMembers.confirm")}
        saving={status === "saving"}
        error={
          status === "failed" ? t("admin.organisation.updateFailed") : null
        }
        onCancel={() => {
          setConfirmingReads(false);
        }}
        onConfirm={async () => {
          if ((await update()) === "changed") {
            setConfirmingReads(false);
          }
        }}
      >
        <Typography variant="body2">
          {t(
            reads
              ? "admin.organisation.readsThroughMembers.confirmOn"
              : "admin.organisation.readsThroughMembers.confirmOff",
            { name: organisation.name },
          )}
        </Typography>
        {changes !== undefined && changes.renameRequest !== null ? (
          <Typography variant="body2">
            {t("admin.organisation.readsThroughMembers.alsoRenamed", {
              name: changes.renameRequest.name,
            })}
          </Typography>
        ) : null}
        {changes !== undefined && changes.setTypeRequest !== null ? (
          <Typography variant="body2">
            {t("admin.organisation.readsThroughMembers.alsoRetyped", {
              type: t(`organisation.type.${changes.setTypeRequest.type}`),
            })}
          </Typography>
        ) : null}
      </ConfirmChangesDialog>
    </Stack>
  );
};

/** A table of what a tab lists, with its loading, failure and empty states. */
const ListingTab = <Row,>({
  description,
  loaded,
  empty,
  failedMessage,
  headings,
  rowKey,
  cells,
}: {
  description: string;
  loaded: Loaded<Row>;
  empty: string;
  failedMessage: string;
  headings: readonly string[];
  rowKey: (row: Row) => string;
  cells: (row: Row) => readonly ReactNode[];
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
      {loaded.failed ? (
        <Typography variant="body2" color="error">
          {failedMessage}
        </Typography>
      ) : loaded.rows === undefined ? (
        <Typography variant="body2" color="text.secondary">
          {t("common.loading")}
        </Typography>
      ) : loaded.rows.length === 0 ? (
        <Typography variant="body2">{empty}</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {headings.map((heading) => (
                  <TableCell key={heading}>{heading}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loaded.rows.map((row) => {
                const rowCells = cells(row);

                return (
                  <TableRow key={rowKey(row)} hover>
                    {headings.map((heading, index) => (
                      <TableCell key={heading}>{rowCells[index]}</TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

/**
 * Runs one change at a time, and remembers whether the last one failed and why.
 *
 * Every control is disabled while a change is in flight, deliberately: the
 * change is followed by a re-read of the whole list, so acting on a second row
 * before it lands would be acting on a list that is about to be replaced.
 */
const useOneChangeAtATime = <Outcome,>(
  failedWith: (outcome: Outcome) => string | null,
) => {
  const [changing, setChanging] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const change = async (apply: () => Promisable<Outcome>): Promise<boolean> => {
    setChanging(true);
    let failed: string | null;

    try {
      failed = failedWith(await apply());
    } catch {
      failed = "";
    }

    setProblem(failed);
    setChanging(false);
    return failed === null;
  };

  const dismissProblem = () => {
    setProblem(null);
  };

  return { changing, problem, change, dismissProblem };
};

const RelationshipsTab = ({
  organisation,
  relationships,
  organisationOptions,
  organisationSearch,
  onOrganisationSearch,
  onRelate,
  onUnrelate,
}: {
  organisation: AdministeredOrganisation;
  relationships: Loaded<Relationship>;
  organisationOptions: readonly Organisation[];
  organisationSearch: string;
  onOrganisationSearch: (query: string) => Promisable<void>;
  onRelate: (
    request: RelateOrganisationRequest,
  ) => Promisable<RelationshipChangeOutcome>;
  onUnrelate: (
    relationshipId: OrganisationRelationshipId,
  ) => Promisable<RelationshipChangeOutcome>;
}) => {
  const { t } = useTranslation();
  const changeable = isOrganisationChangeable(organisation.id);
  const { changing, problem, change, dismissProblem } = useRelationshipChange();
  const [removing, setRemoving] = useState<{
    relationship: Relationship;
    change: RelationshipChange;
  } | null>(null);

  return (
    <>
      <ListingTab
        description={t("admin.organisation.relationships.description")}
        loaded={relationships}
        empty={t("admin.organisation.relationships.none")}
        failedMessage={t("admin.organisation.relationships.loadFailed")}
        headings={[
          t("admin.organisation.relationships.relation"),
          t("admin.organisation.relationships.organisation"),
          ...(changeable ? [""] : []),
        ]}
        rowKey={(relationship) => relationship.id}
        cells={(relationship) => [
          t(
            `admin.organisation.relationships.relations.${relationship.relation}`,
          ),
          <Link
            key="organisation"
            component={RouterLink}
            to={createRoute.adminOrganisation(relationship.organisation.id)}
          >
            {relationship.organisation.name}
          </Link>,
          ...(changeable
            ? [
                <Button
                  key="remove"
                  size="small"
                  color="error"
                  disabled={changing}
                  onClick={() => {
                    dismissProblem();
                    setRemoving({
                      relationship,
                      change: {
                        action: "remove",
                        relation: relationship.relation,
                        otherName: relationship.organisation.name,
                      },
                    });
                  }}
                >
                  {t("admin.organisation.relationships.remove")}
                </Button>,
              ]
            : []),
        ]}
      />

      {removing === null && <RelationshipProblem problem={problem} />}

      <RelationshipChangeDialog
        organisationName={organisation.name}
        change={removing?.change ?? null}
        saving={changing}
        problem={problem}
        onCancel={() => {
          setRemoving(null);
        }}
        onConfirm={async () => {
          if (removing === null) {
            return;
          }
          if (await change(() => onUnrelate(removing.relationship.id))) {
            setRemoving(null);
          }
        }}
      />

      <AddRelationship
        organisation={organisation}
        organisationOptions={organisationOptions}
        organisationSearch={organisationSearch}
        onOrganisationSearch={onOrganisationSearch}
        onRelate={onRelate}
      />
    </>
  );
};

/** An empty problem is a failure with no reason to show: a refusal without words, or no answer at all. */
const useRelationshipChange = () =>
  useOneChangeAtATime<RelationshipChangeOutcome>((outcome) =>
    outcome.outcome === "changed" ? null : (outcome.reason ?? ""),
  );

const RelationshipProblem = ({ problem }: { problem: string | null }) => {
  const { t } = useTranslation();

  return problem === null ? null : (
    <Alert severity="error">
      {problem === ""
        ? t("admin.organisation.relationships.changeFailed")
        : problem}
    </Alert>
  );
};

/**
 * Relates this organisation to another, read as a sentence from this one's
 * side: "Northgate Council is Member of Riverside RCC". Offered nowhere the
 * organisation's links may not change.
 */
export const AddRelationship = ({
  organisation,
  organisationOptions,
  organisationSearch,
  onOrganisationSearch,
  onRelate,
}: {
  organisation: AdministeredOrganisation;
  organisationOptions: readonly Organisation[];
  organisationSearch: string;
  onOrganisationSearch: (query: string) => Promisable<void>;
  onRelate: (
    request: RelateOrganisationRequest,
  ) => Promisable<RelationshipChangeOutcome>;
}) => {
  const { t } = useTranslation();
  const [relation, setRelation] = useState<OrganisationRelation>("member_of");
  // Held as the whole object rather than its id, so the field can render the
  // name after the search that produced it has moved on.
  const [other, setOther] = useState<Organisation | null>(null);
  const { changing, problem, change, dismissProblem } = useRelationshipChange();
  const [confirming, setConfirming] = useState(false);

  if (!isOrganisationChangeable(organisation.id)) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2" component="h2">
        {t("admin.organisation.relationships.add")}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ alignItems: { sm: "center" } }}
      >
        <Typography variant="body1">
          {t("admin.organisation.relationships.addSubject", {
            name: organisation.name,
          })}
        </Typography>
        <TextField
          select
          label={t("admin.organisation.relationships.relation")}
          value={relation}
          size="small"
          sx={{ minWidth: 180 }}
          onChange={(event) => {
            // The options are this schema's own, so a value it rejects
            // cannot come from the select.
            setRelation(OrganisationRelationSchema.parse(event.target.value));
          }}
        >
          {RELATIONS.map((each) => (
            <MenuItem key={each} value={each}>
              {t(`admin.organisation.relationships.relations.${each}`)}
            </MenuItem>
          ))}
        </TextField>
        <Autocomplete
          options={organisationOptions.filter((option) =>
            isOrganisationChangeable(option.id),
          )}
          value={other}
          inputValue={organisationSearch}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
          // The server has already narrowed the options to the search, so
          // filtering them again here would hide rows it deliberately
          // returned.
          filterOptions={(options) => options}
          onInputChange={(_event, value, reason) => {
            // A selection sets the input to the chosen name, which would
            // otherwise be sent back as a search for it.
            if (reason !== "reset") {
              void onOrganisationSearch(value);
            }
          }}
          onChange={(_event, chosen) => {
            setOther(chosen);
          }}
          sx={{ minWidth: 260, flexGrow: 1 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.organisation.relationships.organisation")}
              size="small"
            />
          )}
        />
        <Button
          variant="contained"
          disabled={other === null || changing}
          onClick={() => {
            dismissProblem();
            setConfirming(true);
          }}
        >
          {t("admin.organisation.relationships.addAction")}
        </Button>
      </Stack>
      {!confirming && <RelationshipProblem problem={problem} />}
      <RelationshipChangeDialog
        organisationName={organisation.name}
        change={
          confirming && other !== null
            ? { action: "add", relation, otherName: other.name }
            : null
        }
        saving={changing}
        problem={problem}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={async () => {
          if (other === null) {
            return;
          }
          if (
            await change(() =>
              onRelate(
                RelateOrganisationRequestSchema.parse({
                  relation,
                  organisationId: other.id,
                }),
              ),
            )
          ) {
            setConfirming(false);
            setOther(null);
          }
        }}
      />
    </Stack>
  );
};

const UsersTab = ({
  organisation,
  memberships,
  userOptions,
  userSearch,
  onUserSearch,
  onGrant,
  onRevoke,
}: {
  organisation: AdministeredOrganisation;
  memberships: Loaded<OrganisationUserMembership>;
  userOptions: readonly User[];
  userSearch: string;
  onUserSearch: (query: string) => Promisable<void>;
  onGrant: (
    userId: UserId,
    role: CustomerRole,
  ) => Promisable<OrganisationChangeOutcome>;
  onRevoke: (
    userId: UserId,
    role: CustomerRole,
  ) => Promisable<OrganisationChangeOutcome>;
}) => {
  const { t } = useTranslation();
  const changeable = isOrganisationChangeable(organisation.id);
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<CustomerRole>("assessor");
  const { changing, problem, change } =
    useOneChangeAtATime<OrganisationChangeOutcome>((outcome) =>
      outcome === "changed" ? null : "",
    );

  const alreadyHeld =
    user !== null &&
    (memberships.rows ?? []).some(
      (membership) => membership.userId === user.id && membership.role === role,
    );

  return (
    <>
      <ListingTab
        description={t("admin.organisation.users.description")}
        loaded={memberships}
        empty={t("admin.organisation.users.none")}
        failedMessage={t("admin.organisation.users.loadFailed")}
        headings={[
          t("admin.users.name"),
          t("admin.users.email"),
          t("admin.memberships.role"),
          ...(changeable ? [""] : []),
        ]}
        // One row per membership, so the account alone does not identify a row.
        rowKey={(membership) => `${membership.userId}:${membership.role}`}
        cells={(membership) => {
          // `Role` includes Thalamos's own administrative membership, which the
          // endpoint does not accept, so its row cannot be removed here.
          const removable = CustomerRoleSchema.safeParse(membership.role);

          return [
            <Link
              key="name"
              component={RouterLink}
              to={createRoute.adminUser(membership.userId)}
            >
              {membership.name}
            </Link>,
            membership.email,
            t(`roles.${membership.role}`),
            ...(changeable
              ? [
                  removable.success ? (
                    <Button
                      key="remove"
                      size="small"
                      color="error"
                      disabled={changing}
                      onClick={() => {
                        void change(() =>
                          onRevoke(membership.userId, removable.data),
                        );
                      }}
                    >
                      {t("admin.memberships.revokeAction")}
                    </Button>
                  ) : (
                    t("admin.memberships.reconciled")
                  ),
                ]
              : []),
          ];
        }}
      />

      {problem === null ? null : (
        <Alert severity="error">{t("admin.memberships.changeFailed")}</Alert>
      )}

      {changeable ? (
        <Stack spacing={2}>
          <Typography variant="subtitle2" component="h2">
            {t("admin.memberships.grant")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Autocomplete
              options={[...userOptions]}
              value={user}
              inputValue={userSearch}
              getOptionLabel={(option) => option.name}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack>
                    <span>{option.name}</span>
                    <Typography variant="body2" color="text.secondary">
                      {option.email}
                    </Typography>
                  </Stack>
                </li>
              )}
              isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
              filterOptions={(options) => options}
              onInputChange={(_event, value, reason) => {
                if (reason !== "reset") {
                  void onUserSearch(value);
                }
              }}
              onChange={(_event, chosen) => {
                setUser(chosen);
              }}
              sx={{ minWidth: 260, flexGrow: 1 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("admin.organisation.users.user")}
                  size="small"
                />
              )}
            />
            <TextField
              select
              label={t("admin.memberships.role")}
              value={role}
              size="small"
              sx={{ minWidth: 180 }}
              onChange={(event) => {
                setRole(CustomerRoleSchema.parse(event.target.value));
              }}
            >
              {GRANTABLE_ROLES.map((offered) => (
                <MenuItem key={offered} value={offered}>
                  {t(`roles.${offered}`)}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              disabled={user === null || alreadyHeld || changing}
              sx={{ alignSelf: { sm: "center" } }}
              onClick={() => {
                if (user === null) {
                  return;
                }
                void change(() => onGrant(user.id, role));
              }}
            >
              {t("admin.memberships.grantAction")}
            </Button>
          </Stack>
          {alreadyHeld ? (
            <Typography variant="body2" color="text.secondary">
              {t("admin.organisation.users.alreadyHeld")}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
    </>
  );
};
